// ============================================================
// IN MELEE, AND LEAVING IT (2026-10-26): who a figure is engaged with, and the reminder a move that broke off
// without a Disengage writes. The people half of `battlefield/disengage.ts` (which knows only tokens): foes are
// figures on the other SIDE (`opposedSides`), still in the fight, on the map. Nothing is rolled - the free strike
// is the GM's call (the user's ruling).
//
// Pure. Zero React dependency.
// ============================================================
import { engagedWith, leftMelee } from '../battlefield/disengage';
import { standingAt, type Token } from '../battlefield/tokens';
import { hasCondition } from '../conditions';
import { isInTheFight } from './inTheFight';
import { opposedSides, sideOf, teamOf } from './team';
import type { Combatant } from './types';

type TokenLookup = Readonly<Record<string, Token | undefined>>;

/** The enemies on the map that could strike this figure, as tokens. */
function foeTokens(mover: Combatant, roster: readonly Combatant[], tokens: TokenLookup): Token[] {
  return roster
    .filter((c) => c.id !== mover.id && tokens[c.id] && opposedSides(c, mover) && isInTheFight(c))
    .map((c) => tokens[c.id] as Token);
}

/**
 * Is he engaged in melee with an enemy where he stands? Null when the map cannot say: he is not on it, or he is on
 * no side - a table that never set teams cannot tell friend from foe, so Disengage stays open.
 */
export function isEngaged(
  mover: Combatant | null | undefined,
  roster: readonly Combatant[],
  tokens: TokenLookup | undefined,
): boolean | null {
  const token = mover && tokens?.[mover.id];
  if (!mover || !token || !tokens || !sideOf(teamOf(mover))) return null;
  return engagedWith(token, foeTokens(mover, roster, tokens)).length > 0;
}

/** A finished move's two ends, as the map reports them. */
export interface MoveEnds {
  from?: { x: number; z: number; y?: number } | null;
  to?: { x: number; z: number; y?: number } | null;
}

/**
 * The reminder line when a move left melee without a Disengage, or null. Never names a figure hidden from the
 * table: the line is public.
 */
export function meleeReminder(
  facts: MoveEnds,
  mover: Combatant,
  ctx: { roster: readonly Combatant[]; tokens: TokenLookup | undefined; hiddenIds?: ReadonlySet<string> | null },
): string | null {
  const token = ctx.tokens?.[mover.id];
  if (!token || !ctx.tokens || !facts.from || !facts.to) return null;
  if (hasCondition(mover.state?.conditions, 'Dg')) return null;
  const foes = foeTokens(mover, ctx.roster, ctx.tokens);
  const left = leftMelee(standingAt(token, facts.from), standingAt(token, facts.to), foes);
  const named = left
    .filter((id) => !ctx.hiddenIds?.has(id))
    .map((id) => ctx.roster.find((c) => c.id === id)?.sheet?.name ?? id);
  if (!named.length) return null;
  const who = mover.sheet?.name ?? mover.id;
  return `⚠ ${who} left melee with ${named.join(' and ')} without disengaging - they may strike as he goes (the GM's call).`;
}
